import { omit } from "@msys/common";
import type { TFunction } from "@msys/tolgee";
import { isNil } from "lodash-es";
import React from "react";
import { ProjectStateMachineStatus } from "../../../clients/graphqlTypes.js";
import { ViewerShapeFragment } from "../../auth/UserDataProvider.generated.js";
import {
  FilterChip,
  FilterChipBoolean,
  FilterChipGroup,
} from "../../commons/filters/FilterChip.js";
import { FilterPriceRangeChip } from "../../commons/filters/FilterPriceRangeChip.js";
import { CrmCompaniesOrPersonsFilterChips } from "../crm-companies/CrmCompaniesOrPersonsSelect.js";
import { CrmCompanyFilterChips } from "../crm-companies/CrmCompaniesSelect.js";
import { CrmCompanyFilterChip } from "../crm-companies/CrmCompanyFilterChip.js";
import { FilterCustomFieldsChips } from "../custom-fields/filters/index.js";
import { OrganisationMembersFilterChips } from "../organisations/FilterOrganisationMembersChips.js";
import {
  FilterProjectCategoryChips,
  FilterProjectStateChips,
  FilterProjectTypeChips,
} from "./filters/index.js";
import { FilterAssigneesChips } from "./filters/ProjectAssignees.js";
import { ProjectPhasesFilterChips } from "./filters/ProjectPhases.js";
import { ProjectSourcesFilterChips } from "./filters/ProjectSources.js";
import { Filters } from "./ProjectsList.js";

export function getProjectFilterChipsFactory({
  viewer,
  t,
  getFormattedFloat,
  allowedStates,
  showPhasesChip,
}: {
  viewer: ViewerShapeFragment;
  t: TFunction<"Projects" | "Global" | "Tickets" | "Opportunities">;
  getFormattedFloat: (value: number) => string;
  allowedStates: ProjectStateMachineStatus[];
  showPhasesChip: boolean;
}) {
  return function getProjectFilterChips<F extends Filters>({
    filters,
    setFilters,
  }: {
    filters: F;
    setFilters?: React.Dispatch<React.SetStateAction<F>>;
  }): Array<JSX.Element> {
    const chips: Array<JSX.Element> = [];

    if (
      filters.filterRelevantToUserIds &&
      filters.filterRelevantToUserIds.length > 0 &&
      filters.filterRelevantToUserIds.includes(viewer.id)
    ) {
      chips.push(
        <FilterChip
          key="relevant-to-me"
          onDelete={
            setFilters
              ? () => {
                  setFilters(filters => {
                    let newRelevantIds = [
                      ...(filters.filterRelevantToUserIds ?? []),
                    ];
                    if (newRelevantIds.includes(viewer.id))
                      newRelevantIds = newRelevantIds.filter(
                        id => id !== viewer.id
                      );
                    return {
                      ...filters,
                      filterRelevantToUserIds:
                        newRelevantIds.length > 0 ? newRelevantIds : undefined,
                    };
                  });
                }
              : undefined
          }
          value={t("Relevant to me", { ns: "Opportunities" })}
        />
      );
    }

    if (filters.filterCategories) {
      chips.push(
        <FilterProjectCategoryChips
          key="categories"
          values={filters?.filterCategories}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterCategories: newValue,
                  }))
              : undefined
          }
        />
      );
    }

    if (filters.filterType) {
      chips.push(
        <FilterProjectTypeChips
          key="types"
          values={filters?.filterType}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterType: newValue,
                  }))
              : undefined
          }
        />
      );
    }

    if (filters.filterBuildingZip) {
      chips.push(
        <FilterChip
          key="building-zip"
          label={t("ZIP", { ns: "Projects" })}
          value={filters?.filterBuildingZip}
          onDelete={
            setFilters
              ? () =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterBuildingZip: "", // FIXME: remove instead
                  }))
              : undefined
          }
        />
      );
    }

    if (filters.filterMinBudget || filters.filterMaxBudget) {
      chips.push(
        <FilterPriceRangeChip
          key="budget"
          label={t("Budget", { ns: "Projects" })}
          valueMin={filters?.filterMinBudget ?? null}
          valueMax={filters?.filterMaxBudget}
          onDelete={
            setFilters
              ? () => {
                  setFilters(f => ({
                    ...f,
                    filterMinBudget: null,
                    filterMaxBudget: null,
                  }));
                }
              : undefined
          }
        />
      );
    }

    if (filters.filterWithinRadiusKm) {
      chips.push(
        <FilterChip
          key="proximity-km"
          label={t("Proximity (km)", { ns: "Projects" })}
          value={filters?.filterWithinRadiusKm}
          onDelete={
            setFilters
              ? () =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterWithinRadiusKm: 0, // FIXME: remove instead
                  }))
              : undefined
          }
          getValueLabel={value => getFormattedFloat(value)}
        />
      );
    }

    if (filters.filterUnassigned) {
      chips.push(
        <FilterChip
          key="unassigned"
          value={t("Unassigned", { ns: "Projects" })}
          onDelete={
            setFilters
              ? () =>
                  setFilters(filters => ({
                    ...filters,
                    filterUnassigned: undefined,
                  }))
              : undefined
          }
        />
      );
    }

    if (filters.filterAssigneeIds) {
      chips.push(
        <FilterAssigneesChips
          key="assignees"
          values={filters?.filterAssigneeIds}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filters => ({
                    ...filters,
                    filterAssigneeIds: newValue,
                  }))
              : undefined
          }
        />
      );
    }

    if (!isNil(filters.filterPlanningState)) {
      chips.push(
        <FilterChipGroup
          key="planning-state"
          label={t("Planned", { ns: "Projects" })}
          values={filters.filterPlanningState}
          setValues={
            setFilters
              ? value =>
                  setFilters(filters => ({
                    ...filters,
                    filterPlanningState: value,
                  }))
              : undefined
          }
        />
      );
    }

    if (!isNil(filters.filterOverdue)) {
      chips.push(
        <FilterChipBoolean
          key="overdue"
          label={t("Overdue", { ns: "Tickets" })}
          value={filters.filterOverdue}
          onDelete={
            setFilters
              ? () =>
                  setFilters(filters => ({
                    ...filters,
                    filterOverdue: undefined, // FIXME: remove instead
                  }))
              : undefined
          }
        />
      );
    }

    if (!isNil(filters.filterUrgent)) {
      chips.push(
        <FilterChipBoolean
          key="urgent"
          label={t("Urgent", { ns: "Tickets" })}
          value={filters.filterUrgent}
          onDelete={
            setFilters
              ? () =>
                  setFilters(filters => ({
                    ...filters,
                    filterUrgent: undefined, // FIXME: remove instead
                  }))
              : undefined
          }
        />
      );
    }

    if (
      filters?.filterIncludeState &&
      filters?.filterIncludeState?.length > 0
    ) {
      chips.push(
        <FilterProjectStateChips
          key="state"
          values={filters?.filterIncludeState}
          setValues={
            setFilters
              ? newValue =>
                  newValue && newValue.length > 0
                    ? setFilters(filters => ({
                        ...filters,
                        filterIncludeState: newValue,
                      }))
                    : setFilters(
                        filters => omit(filters, "filterIncludeState") as F
                      )
              : undefined
          }
        />
      );
    }

    if (showPhasesChip && filters?.filterPhaseIds?.length) {
      chips.push(
        <ProjectPhasesFilterChips
          key="phases"
          values={filters?.filterPhaseIds}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filters => ({
                    ...filters,
                    filterPhaseIds: newValue,
                  }))
              : undefined
          }
          allowedStates={allowedStates}
        />
      );
    }

    if (filters?.filterByCustomFields) {
      chips.push(
        <FilterCustomFieldsChips
          key="custom-fields"
          values={filters?.filterByCustomFields ?? []}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterByCustomFields: newValue,
                  }))
              : undefined
          }
        />
      );
    }

    if (filters?.filterByCrmOrganisationId) {
      chips.push(
        <CrmCompanyFilterChip
          key="crm-organisation"
          value={filters?.filterByCrmOrganisationId}
          onDelete={
            setFilters
              ? () =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterByCrmOrganisationId: null,
                  }))
              : undefined
          }
          label={t("Client", { ns: "Projects" })}
        />
      );
    }

    if (filters?.filterByCrmLinkIds?.length) {
      chips.push(
        <CrmCompaniesOrPersonsFilterChips
          key="crm-links"
          value={filters?.filterByCrmLinkIds}
          setValue={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterByCrmLinkIds: newValue,
                  }))
              : undefined
          }
          label={t("Contact", { ns: "Projects" })}
        />
      );
    }

    if (filters?.filterBySourceUserIds?.length) {
      chips.push(
        <OrganisationMembersFilterChips
          key="source-users"
          values={filters?.filterBySourceUserIds}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterBySourceUserIds: newValue,
                  }))
              : undefined
          }
          label={t("Created by", { ns: "Projects" })}
        />
      );
    }

    if (filters?.filterBySourceCrmOrganisationIds) {
      chips.push(
        <CrmCompanyFilterChips
          key="source-crm-organisations"
          value={filters?.filterBySourceCrmOrganisationIds}
          setValue={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    filterBySourceCrmOrganisationIds: newValue,
                  }))
              : undefined
          }
          label={t("Created by", { ns: "Projects" })}
        />
      );
    }

    if (filters?.sources?.length) {
      chips.push(
        <ProjectSourcesFilterChips
          key="sources"
          values={filters?.sources}
          setValues={
            setFilters
              ? newValue =>
                  setFilters(filterVariables => ({
                    ...filterVariables,
                    sources: newValue,
                  }))
              : undefined
          }
        />
      );
    }

    return chips;
  };
}
